// SupportTicketsPage.jsx — CONTAINER (dono da lógica) da tela /support-tickets. Monta { vm, actions }
// reais e renderiza a SupportTicketsView (View pura do Claude Design). Formato View/ViewModel.
// Substitui o antigo components/SupportTickets/SupportTicketsPage (apagado em 27/10/2026; está no histórico do Git).
import { useState, useEffect, useMemo, useCallback } from 'react';
import DOMPurify from 'dompurify';

import useSupportTickets from '../../hooks/useSupportTickets';
import { formatDateTime, formatRelativeDistance } from '../../utils/formatters';
import SupportTicketsView from './SupportTicketsView';

// Enums da API (v2) → rótulo + tom themeável. 'default' vira 'neutral' (papel do tema, não cor MUI).
const STATUS_MAP = {
  0: { label: 'Aberto', tone: 'info' }, Open: { label: 'Aberto', tone: 'info' }, open: { label: 'Aberto', tone: 'info' },
  1: { label: 'Em Análise', tone: 'warning' }, InProgress: { label: 'Em Análise', tone: 'warning' }, inProgress: { label: 'Em Análise', tone: 'warning' },
  2: { label: 'Resolvido', tone: 'success' }, Resolved: { label: 'Resolvido', tone: 'success' }, resolved: { label: 'Resolvido', tone: 'success' },
  3: { label: 'Fechado', tone: 'neutral' }, Closed: { label: 'Fechado', tone: 'neutral' }, closed: { label: 'Fechado', tone: 'neutral' },
  4: { label: 'Aguardando Cliente', tone: 'warning' }, WaitingCustomer: { label: 'Aguardando Cliente', tone: 'warning' }, waitingCustomer: { label: 'Aguardando Cliente', tone: 'warning' },
  5: { label: 'Aguardando Suporte', tone: 'error' }, WaitingSupport: { label: 'Aguardando Suporte', tone: 'error' }, waitingSupport: { label: 'Aguardando Suporte', tone: 'error' },
};
const PRIORITY_MAP = {
  0: { label: 'Baixa', tone: 'info', arrow: '↓', show: true }, Low: { label: 'Baixa', tone: 'info', arrow: '↓', show: true }, low: { label: 'Baixa', tone: 'info', arrow: '↓', show: true },
  1: { label: 'Normal', tone: 'neutral', arrow: '', show: false }, Normal: { label: 'Normal', tone: 'neutral', arrow: '', show: false }, normal: { label: 'Normal', tone: 'neutral', arrow: '', show: false },
  2: { label: 'Alta', tone: 'error', arrow: '↑', show: true }, High: { label: 'Alta', tone: 'error', arrow: '↑', show: true }, high: { label: 'Alta', tone: 'error', arrow: '↑', show: true },
  3: { label: 'Urgente', tone: 'error', arrow: '↑', show: true }, Urgent: { label: 'Urgente', tone: 'error', arrow: '↑', show: true }, urgent: { label: 'Urgente', tone: 'error', arrow: '↑', show: true },
};
const TYPE_MAP = {
  suggestion: { label: '💡 Sugestão', tone: 'info' },
  complaint: { label: '😠 Reclamação', tone: 'error' },
  question: { label: '❓ Pergunta', tone: 'warning' },
  bug: { label: '🐛 Relatório de Bug', tone: 'error' },
  feedback: { label: '💬 Feedback', tone: 'success' },
  support: { label: '🆘 Suporte', tone: 'primary' },
  other: { label: 'Outro', tone: 'neutral' },
};
const DEFAULT_PRIORITY = { label: 'Normal', tone: 'neutral', arrow: '', show: false };

export default function SupportTicketsPage() {
  const st = useSupportTickets();
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [replyContent, setReplyContent] = useState('');

  const fetchPage = useCallback((pageNum = 1) => st.fetchTickets({ page: pageNum, pageSize: st.pageSize }), [st]);

  useEffect(() => { fetchPage(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dispara o widget flutuante de novo ticket (fora desta tela).
  const openWidget = useCallback(() => {
    const btn = document.querySelector('.agendario-support-widget-button');
    if (btn && typeof btn.click === 'function') btn.click();
  }, []);

  // ── ViewModel ────────────────────────────────────────────────────────────────
  const tickets = useMemo(() => (st.tickets || []).map((tk) => {
    const status = STATUS_MAP[tk.status] || { label: 'Desconhecido', tone: 'neutral' };
    const priority = PRIORITY_MAP[tk.priority] || DEFAULT_PRIORITY;
    const type = tk.ticketType ? (TYPE_MAP[String(tk.ticketType).toLowerCase()] || { label: tk.ticketType, tone: 'neutral' }) : null;
    const expanded = tk.id === selectedTicketId;
    const rc = tk.repliesCount || 0;
    const uc = tk.unreadAdminReplies || 0;

    let detail = null;
    if (expanded && st.selectedTicket) {
      const replies = (st.replies || []).map((r) => ({
        id: r.id,
        author: r.isAdminReply ? 'support' : 'you',
        authorLabel: r.isAdminReply ? '👤 Suporte' : '👤 Você',
        createdRelative: r.createdAt ? formatRelativeDistance(r.createdAt) : '',
        contentHtml: DOMPurify.sanitize(r.content || ''),
      }));
      detail = {
        message: st.selectedTicket.message || '',
        contactInfo: st.selectedTicket.contactInfo || null,
        createdAtLabel: st.selectedTicket.createdAt ? formatDateTime(st.selectedTicket.createdAt) : '',
        replies,
        repliesEmpty: replies.length === 0,
      };
    }

    return {
      id: tk.id,
      idShort: (tk.id || '').substring(0, 8),
      subject: tk.subject || 'Sem assunto',
      statusLabel: status.label,
      statusTone: status.tone,
      priority: { show: priority.show, label: priority.label, tone: priority.tone, arrow: priority.arrow },
      typeLabel: type?.label || null,
      typeTone: type?.tone || 'neutral',
      createdRelative: tk.createdAt ? formatRelativeDistance(tk.createdAt) : '',
      repliesCount: rc,
      repliesLabel: rc > 0 ? `${rc} resposta${rc !== 1 ? 's' : ''}` : null,
      unreadCount: uc,
      unreadLabel: uc > 0 ? `${uc} nova${uc !== 1 ? 's' : ''}` : null,
      expanded,
      detailsLoading: expanded && st.ticketDetailsLoading,
      detail,
    };
  }), [st.tickets, st.selectedTicket, st.replies, st.ticketDetailsLoading, selectedTicketId]);

  const ticketsEmpty = tickets.length === 0;
  const viewState = (st.listLoading && ticketsEmpty) ? 'loading'
    : (st.listError && ticketsEmpty) ? 'error'
    : ticketsEmpty ? 'empty'
    : 'ready';

  const vm = {
    viewState,
    errorMessage: st.listError || null,
    title: 'Tickets de Suporte',
    subtitle: 'Acompanhe suas solicitações e converse com o suporte',
    refreshLabel: 'Atualizar',
    newLabel: 'Novo Ticket',
    refreshing: !!st.listLoading,
    emptyMessage: 'Você ainda não possui tickets de suporte. Clique em "Novo Ticket" para criar um.',
    tickets,
    replyEditor: {
      visible: !!selectedTicketId,
      content: replyContent,
      submitting: !!st.replySubmitting,
      error: st.replyError || null,
    },
    pagination: {
      page: st.page || 1,
      pageCount: Math.max(1, Math.ceil((st.totalCount || 0) / (st.pageSize || 20))),
      total: st.totalCount || 0,
    },
  };

  // ── Actions ──────────────────────────────────────────────────────────────────
  const handleToggle = useCallback(async (ticketId) => {
    setReplyContent('');
    if (ticketId === null) { setSelectedTicketId(null); return; }
    setSelectedTicketId(ticketId);
    await st.fetchTicketDetails(ticketId);
  }, [st]);

  const handleSubmitReply = useCallback(async () => {
    if (!selectedTicketId || !replyContent.trim()) return;
    try {
      await st.submitReply(selectedTicketId, replyContent);
      setReplyContent('');
      await st.fetchTicketDetails(selectedTicketId);
    } catch { /* replyError já é exposto pelo hook */ }
  }, [selectedTicketId, replyContent, st]);

  const actions = {
    onRefresh: () => fetchPage(1),
    onNewTicket: openWidget,
    onToggleTicket: handleToggle,
    onChangePage: (p) => fetchPage(p),
    onChangeReply: setReplyContent,
    onSubmitReply: handleSubmitReply,
    onRetry: () => fetchPage(1),
  };

  return <SupportTicketsView vm={vm} actions={actions} />;
}
